<template>
  <div class="dropdown user-menu" data-bs-auto-close="outside">
    <button type="button" class="btn btn-inline dropdown-toggle" id="userDropdown" role="button" data-bs-toggle="dropdown" aria-expanded="false">
      <span class="badge test-user" v-if="userRef?.isTestUser">
        Test
      </span>
      <span class="badge admin-user" v-if="tokenManager.current.isPrivilegedUser">
        Admin        
      </span>
      <span class="badge impersonated" v-if="tokenManager.current.actorId">
        Acting
      </span>
      <span class="badge demo-user" v-if="tokenManager.current.userPrivileges.includes(UserPrivilege.DemoUser)">
        Demo
      </span>
      <img src="@/assets/icons/person_gray_150.png" class="user-icon"/>
      <span class="user-name">
        {{ userRef?.displayName }}
      </span>
    </button>
    <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="userDropdown">
      <li class="hide-md">
        <div class="dropdown-item disabled user-name">
          <img src="@/assets/icons/person_gray_150.png" class="user-icon"/>
          {{ userRef?.displayName }}
        </div>
      </li>
      <li class="hide-md"><hr class="dropdown-divider"></li>
      <li>
        <router-link class="dropdown-item" to="/account">
          Account
        </router-link>
      </li>
      <li><hr class="dropdown-divider"></li>
      <li>
        <router-link class="dropdown-item" to="/trees">Trees</router-link>
      </li>
      <li>
        <router-link class="dropdown-item" to="/groups">
          Groups
          <span v-if="notificationCountRef > 0" class="badge notify-badge rounded-pill">
            {{ notificationCountRef }}
          </span>
        </router-link>
      </li>
      <li v-if="!tokenManager.current.isSupervised">
        <router-link class="dropdown-item" to="/profiles">
          Managed profiles
        </router-link>
      </li>
      <li v-if="supervisedUsersRef.length > 0"><hr class="dropdown-divider"></li>
      <li v-if="supervisedUsersRef.length > 0" class="dropdown-header">
        Supervised users
      </li>
      <li v-for="su in supervisedUsersRef" :key="su.id" class="">
        <router-link class="dropdown-item" :to="User.getPageUrl(su.id)">
          {{ su.displayName }}
        </router-link>
      </li>
      <li v-if="tokenManager.current.isPrivilegedUser"><hr class="dropdown-divider"></li>
      <li v-if="tokenManager.current.canReadUsers">
        <router-link class="dropdown-item admin" to="/admin">Admin</router-link>
      </li>
      <li v-if="tokenManager.current.canReadUsers">
        <router-link class="dropdown-item admin" to="/admin/users">Manage users</router-link>
      </li>
      <li v-if="tokenManager.current.canReadUsers">
        <router-link class="dropdown-item admin" to="/admin/identities">Manage identities</router-link>
      </li>
      <li><hr class="dropdown-divider"></li>
      <li v-if="showRefreshTokenRef">
        <button type="button" class="dropdown-item dev-only" :disabled="!tokenManager.current.canRefresh" @click="onRefreshToken">
          Refresh access token
        </button>
      </li>
      <li v-if="showRevertRef">
        <button type="button" class="dropdown-item admin" @click="onRevert">
          Revert to original user
        </button>
      </li>
      <li v-else>
        <router-link class="dropdown-item" to="/signout">
          Sign out
        </router-link>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.dropdown-toggle {
  display: flex;
  gap: 0.25rem;
  align-items: center;

  .user-name {
    display: none;
  }

  @media (min-width: 992px) {
    .user-name {
      display: inline;
      color: rgba(0, 0, 0, 0.8);
    }

    .badge {
      order: 1;
    }
  }
}

img.user-icon {
  height: 30px;
  border-radius: 50%;
  background-color: #ddd;
  padding: 4px;
  opacity: 60%;
}

.badge {
  &.test-user {
    margin-left: 0.25em;
    background-color: var(--bs-warning);
    color: #888;
  }

  &.admin-user {
    background-color: var(--bs-danger);
    color: white;
  }

  &.impersonated {
    background-color: var(--bs-warning);
  }

  &.demo-user {
    background-color: var(--bs-info);
    color: white;
  }
}

.dropdown-toggle .notify-badge {
  top: 4px;
  right: -12px;
}

.dropdown-menu {
  min-width: 250px;
}

#userDropdown::after {
  display: none;
}
</style>

<script lang="ts" setup>
import { computed } from 'vue'
import { useUserStore } from '@/gp/UserStore'
import { useUserSupervisorStore } from '@/gp/UserSupervisorStore'
import { useFamilyGroupInviteStore } from '@/gp/FamilyGroupInviteStore'
import { LoadMode } from '@/util/AsyncData'
import { useTokenManager, UserPrivilege } from '@/auth/TokenManager'
import { User } from '@/gp/GroupAdminModel'
import { Environment } from '@/util/Environment'
import { InvitationResult } from '@/gp/GroupAdminModel'

const userStore = useUserStore()
const userSupervisorStore = useUserSupervisorStore()
const inviteStore = useFamilyGroupInviteStore()
const tokenManager = useTokenManager()

const userRef = computed(() => userStore.getAsyncSelf(LoadMode.EnsureLoaded)?.data)
const supervisedUsersRef = computed(() => {
  if (tokenManager.current.isSupervised)
    return [] // can't supervise others, and API call is forbidden anyway
  const sups = userSupervisorStore.getForSupervisor(tokenManager.current.userId, LoadMode.EnsureLoaded)
  return userStore.getLoadedUserList(sups.map(s => s.forUserId!))
})

const showRefreshTokenRef = computed(() => Environment.isDevelopment())
const showRevertRef = computed(() => {
  // TODO: allow reverting from supervised account if original was admin (not just supervisor)
  return tokenManager.current.actorId && !tokenManager.current.isSupervised
})

const pendingInvitesRef = computed(() => inviteStore.getInvitesForUser(undefined, LoadMode.EnsureLoaded)
  .filter(inv => inv.result == InvitationResult.Pending))

const notificationCountRef = computed(() => pendingInvitesRef.value.length)

async function onRefreshToken() {
  await tokenManager.refreshAsync()
}

function onRevert() {
  tokenManager.revert()
}
</script>